import { useEffect, useState } from 'react'

/**
 * 先摆一张模糊的，原图到了再无声换掉。
 *
 * 背景：2026-09-12 实测，一张 2.6 MB 的原图从首尔那台取到浏览器要 41 秒
 *   （跨境线路，README 记的 35 到 60 KB/s；同一张图的缩略图 42 KB 用 0.33 秒）。
 *   刷新一个已经跑完诊断的页面时，标红那一屏就这样开着半分多钟：红框已经画好了，
 *   压在一张只画到三分之一的图上——用户看到的是我们把他的图标坏了。
 * 设计意图：占位图由服务端现做（`?placeholder=1`，见 app/api/jobs.py 的 `_placeholder`），
 *   **尺寸与原图一致**，所以这里只用换一个 `src`：图框宽高不动，压在图上那层
 *   按框尺寸算坐标的红框标记也不用重算。等原图 decode 完再换，换的是一张已经
 *   在缓存里、解码好的图，同一帧就位，看不见闪白。
 * 关键约束：
 *   1. 只换 src，不叠两层。叠层是给「内容不同的两张图」用的（见 YourImage 的
 *      useLiveFrames：过程图每一步都是新画面，要淡入才看得出在变）。这里两张是
 *      同一张画面的两个清晰度，淡入反而多一次重绘。
 *   2. 没有占位图（本地 blob、录像态、`soft` 与 `full` 同址）时原样返回 `full`，
 *      一次多余的请求都不发。
 *   3. 拿不到原图（网断了、被清理了）就一直停在占位图上：糊着的画面仍然说明
 *      「你的图是这一张」，比一个破图图标好。
 */
export function useSharpened(full: string, soft?: string | null): { src: string; soft: boolean } {
  const usable = !!soft && soft !== full
  const [sharp, setSharp] = useState(!usable)

  useEffect(() => {
    if (!usable) {
      setSharp(true)
      return
    }
    setSharp(false)
    let dead = false
    const done = () => { if (!dead) setSharp(true) }
    const img = new Image()
    // 拿到了还要等它解码完再换：只等 onload 的话，换 src 那一下解码发生在主线程上，
    // 4K 图能卡出一次掉帧。decode 不被支持或者解码报错时照常换——字节已经到了。
    img.onload = () => { img.decode?.().then(done, done) ?? done() }
    img.onerror = () => { /* 取不到就停在占位图上，见上面第 3 条 */ }
    img.src = full
    return () => { dead = true }
  }, [full, usable])

  return { src: sharp || !usable ? full : soft!, soft: usable && !sharp }
}
